import { useEffect, useRef } from "react";
import type { ConnectorWithOAuth } from "../../../shared/connectors/getTargetValue";
import type { ConnectorOAuthConfig } from "../../../shared/connectors/types";
import { ExpectedError } from "../../../shared/expectedError";
import { useSessionTenantApiOrigin } from "../../hooks/useSessionTenantOrigin";
import { connectorOauthStartPath } from "../../lib/apiPaths";
import { connectorOauthRedirectUri } from "../../lib/tenantOrigin";
import {
  managedOAuthOrigin,
  managedOAuthRedirectUri,
  managedOAuthUrl,
} from "../../../shared/connectors/managedOAuth";

const width = 1000;
const height = 700;

const listenerInterval = 50;

function centerParams(): string {
  const left = Math.round((window.screen.width - width) / 2);
  const top = Math.round((window.screen.height - height) / 2);
  return `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`;
}

export function useOauthPopupWindow() {
  const requestOrigin = window.location.origin;
  const apiOrigin = useSessionTenantApiOrigin();
  const popup = useRef<Window | null>(null);
  const interval = useRef<number | null>(null);
  const listener = useRef<((event: MessageEvent) => void) | null>(null);

  useEffect(() => {
    return () => {
      if (listener.current) removeEventListener("message", listener.current);
      if (interval.current) clearInterval(interval.current);
      if (popup.current && !popup.current.closed) popup.current.close();
    };
  }, []);

  const run = async <T>(
    url: string,
    parseMessage: (event: MessageEvent, openedPopup: Window) => T | null,
    expectedOrigin: string
  ): Promise<T | null> => {
    popup.current = window.open(url, "oauth", centerParams());
    if (!popup.current) return null;
    const openedPopup = popup.current;

    return new Promise((resolve) => {
      const parse = (event: MessageEvent) => {
        if (event.origin !== expectedOrigin) return;
        // Any script can postMessage to our window, so a message the parser
        // rejects must not escape this listener and wedge the popup.
        let parsed: T | null;
        try {
          parsed = parseMessage(event, openedPopup);
        } catch {
          return;
        }
        if (parsed) {
          removeEventListener("message", parse);
          if (listener.current === parse) listener.current = null;
          openedPopup.close();
          clearInterval(pollingInterval);
          if (interval.current === pollingInterval) interval.current = null;
          resolve(parsed);
        }
      };

      listener.current = parse;
      addEventListener("message", parse);

      const pollingInterval = window.setInterval(() => {
        if (openedPopup.closed) {
          removeEventListener("message", parse);
          if (listener.current === parse) listener.current = null;
          clearInterval(pollingInterval);
          if (interval.current === pollingInterval) interval.current = null;
          resolve(null);
        }
      }, listenerInterval);
      interval.current = pollingInterval;
    });
  };

  // Runs a popup whose terminal page posts closePopupWindow's bound result.
  async function flow(
    url: string,
    expectedOrigin: string,
    expected: { provider: string; connectedAppId: string }
  ): Promise<
    { success: true; installationId?: string } | { error: string } | null
  > {
    return run(
      url,
      (event, openedPopup) => {
        if (event.source !== openedPopup) return null;
        const data = (
          typeof event.data === "string" ? JSON.parse(event.data) : event.data
        ) as {
          status?: string;
          message?: string;
          installationId?: string;
          provider?: string;
          connectedAppId?: string;
        } | null;
        if (
          data?.provider !== expected.provider ||
          (data?.status === "success"
            ? data.connectedAppId !== expected.connectedAppId
            : data?.connectedAppId !== undefined &&
              data.connectedAppId !== expected.connectedAppId)
        ) {
          return null;
        }
        if (data?.status === "error")
          return { error: data.message ?? "Setup failed" };
        if (data?.status === "success")
          return {
            success: true as const,
            installationId: data.installationId,
          };
        return null;
      },
      expectedOrigin
    );
  }

  async function auth(
    oauthConfig: ConnectorOAuthConfig,
    app: ConnectorWithOAuth,
    identifier: string
  ) {
    const { clientCredentialsFrom } = oauthConfig;
    if (clientCredentialsFrom === "env") {
      const result = await run(
        managedOAuthUrl(identifier),
        (event) => {
          if (
            typeof event.data === "object" &&
            event.data?.type === "tines_connect_request"
          ) {
            (event.source as Window | null)?.postMessage(
              { type: "tines_connect_response" },
              event.origin
            );
            return null;
          }
          const parsed = JSON.parse(JSON.parse(event.data)[0].value);
          const code = parsed.managedOauthCode as string;
          if (!code) return null;
          const configuration = parsed.metadata as
            | Record<string, string>
            | undefined;
          return { code, configuration };
        },
        managedOAuthOrigin
      );
      if (!result) return null;
      const redirectUri = managedOAuthRedirectUri(identifier);
      return {
        code: result.code,
        redirectUri,
        configuration: result.configuration,
      };
    } else {
      const startUrl = new URL(
        `${requestOrigin}${connectorOauthStartPath(identifier)}`
      );
      startUrl.searchParams.set("app_id", app.id);
      // The success page is served at the tenant callback URL the server
      // minted (getRedirectURI), so listen on that origin, not the app's.
      const callbackOrigin = new URL(
        connectorOauthRedirectUri(identifier, apiOrigin)
      ).origin;
      const result = await flow(startUrl.toString(), callbackOrigin, {
        provider: identifier,
        connectedAppId: app.id,
      });
      if (!result) return null;
      if ("error" in result) throw new ExpectedError(result.error);
      return { managed: true as const };
    }
  }

  return { auth, flow };
}
